import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  CognitoUserPool,
  CognitoUser
} from 'amazon-cognito-identity-js';

// Cognito pool setup
const poolData = {
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
  ClientId: process.env.REACT_APP_COGNITO_USER_POOL_CLIENT_ID,
};

const userPool = new CognitoUserPool(poolData);

// Create the context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth Provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check for existing session on app load
  useEffect(() => {
    checkAuthState();
  }, []);

  const checkAuthState = () => {
    const currentUser = userPool.getCurrentUser();
    
    if (currentUser) {
      currentUser.getSession((err, session) => {
        if (err) {
          console.error('Session error:', err);
          setLoading(false);
          return;
        }
        
        if (session && session.isValid()) {
          setSession(session);
          setUser(currentUser);
          setIsAuthenticated(true);
          console.log('Valid session found:', session);
          
          // Fetch user attributes
          currentUser.getUserAttributes((attrErr, attributes) => {
            if (!attrErr && attributes) {
              const userAttributes = {};
              attributes.forEach(attribute => {
                userAttributes[attribute.getName()] = attribute.getValue();
              });
              setUserInfo(userAttributes);
              console.log('User attributes loaded:', userAttributes);
            }
          });
        } else {
          console.log('No valid session found');
        }
        setLoading(false);
      });
    } else {
      console.log('No current user found');
      setLoading(false);
    }
  };

  // Login function
  const login = (userSession, cognitoUser) => {
    setSession(userSession);
    setUser(cognitoUser);
    setIsAuthenticated(true);
    console.log('User logged in:', userSession);
    
    // Fetch user attributes immediately after login
    cognitoUser.getUserAttributes((err, attributes) => {
      if (!err && attributes) {
        const userAttributes = {};
        attributes.forEach(attribute => {
          userAttributes[attribute.getName()] = attribute.getValue();
        });
        setUserInfo(userAttributes);
        console.log('User attributes loaded after login:', userAttributes);
      }
    });
  };

  // Logout function
  const logout = () => {
    const currentUser = userPool.getCurrentUser();
    if (currentUser) {
      currentUser.signOut();
    }
    setSession(null);
    setUser(null);
    setUserInfo(null);
    setIsAuthenticated(false);
    console.log('User logged out');
  };

  // Get current session token for API calls
  const getSessionToken = () => {
    if (session && session.isValid()) {
      return session.getIdToken().getJwtToken();
    }
    return null;
  };

  // Get access token for API calls
  const getAccessToken = () => {
    if (session && session.isValid()) {
      return session.getAccessToken().getJwtToken();
    }
    return null;
  };

  // Get user attributes
  const getUserAttributes = () => {
    return new Promise((resolve, reject) => {
      if (!user) {
        reject(new Error('No user logged in'));
        return;
      }

      user.getUserAttributes((err, attributes) => {
        if (err) {
          reject(err);
          return;
        }

        const userInfo = {};
        attributes.forEach(attribute => {
          userInfo[attribute.getName()] = attribute.getValue();
        });
        resolve(userInfo);
      });
    });
  };

  // Refresh session
  const refreshSession = () => {
    return new Promise((resolve, reject) => {
      const currentUser = userPool.getCurrentUser();
      
      if (!currentUser) {
        reject(new Error('No user logged in'));
        return;
      }

      currentUser.getSession((err, session) => {
        if (err) {
          reject(err);
          return;
        }

        if (session && session.isValid()) {
          setSession(session);
          resolve(session);
        } else {
          reject(new Error('Invalid session'));
        }
      });
    });
  };

  // Check if session is valid
  const isSessionValid = () => {
    return session && session.isValid();
  };

  const value = {
    user,
    session,
    userInfo,
    isAuthenticated,
    loading,
    login,
    logout,
    getSessionToken,
    getAccessToken,
    getUserAttributes,
    refreshSession,
    isSessionValid,
    checkAuthState
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 